import { createContext, useContext } from "react"
import type { ComponentProps } from "react"
import { createPortal } from "react-dom"
import { DialogFooter } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"

/**
 * The appointment dialog's footer region: a strip under its scrolling body, so the buttons stay in
 * view however long the booking runs. Null outside the dialog, and for the one render before the
 * region has mounted.
 */
export const AppointmentFooterSlot = createContext<HTMLElement | null>(null)

/**
 * A footer for the appointment dialog, drawn into its pinned footer region rather than where it is
 * rendered, so the buttons stay pinned under a long booking.
 *
 * Falls back to drawing in place when there is no region to draw into — which is how an armed
 * action's footer lands at the foot of its own stacked dialog, where the slot is set to null.
 */
export function AppointmentDialogFooter({ className, ...props }: ComponentProps<typeof DialogFooter>) {
  const slot = useContext(AppointmentFooterSlot)
  // Wraps rather than pushing the dialog sideways when there are more buttons than fit on a line.
  const footer = <DialogFooter className={cn("flex-wrap sm:items-center", className)} {...props} />
  return slot ? createPortal(footer, slot) : footer
}
